import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import { motion } from 'framer-motion';
import { QrCode, FileImage, Copy, Check, Download } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { TabBar, TabItem } from '../UI/TabBar';
import { A4PosterDesigner } from './A4PosterModal';
import { getPortalUrl } from '../../lib/portal';
import { clayTapScale } from '../../constants/animations';
import styles from './ShareAlbumModal.module.css';

type ShareTab = 'link' | 'poster';

const TABS: TabItem<ShareTab>[] = [
  { id: 'link', label: 'QR & Link', icon: QrCode },
  { id: 'poster', label: 'Poster', icon: FileImage },
];

interface ShareAlbumModalProps {
  isOpen: boolean;
  onClose: () => void;
  albumId: string;
  albumName: string;
  eventName: string;
  inviteToken: string;
}

/**
 * ShareAlbumModal — everything needed to get guests into an album: the QR
 * code, the invite link, and the printable A4 poster designer.
 */
export function ShareAlbumModal({
  isOpen,
  onClose,
  albumId,
  albumName,
  eventName,
  inviteToken,
}: ShareAlbumModalProps) {
  const [tab, setTab] = useState<ShareTab>('link');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);

  const inviteUrl = `${getPortalUrl('viewer')}/join/${inviteToken}`;

  // Redraw whenever the QR tab becomes visible — the canvas is unmounted
  // while the poster tab is showing, so the ref is null until it returns.
  useEffect(() => {
    if (!isOpen || tab !== 'link' || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, inviteUrl, {
      width: 260,
      margin: 2,
      color: { dark: '#140708', light: '#ffffff' },
      errorCorrectionLevel: 'Q',
    }).catch(() => toast.error('Failed to render QR code'));
  }, [isOpen, tab, inviteUrl]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDownloadQr = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'invite-qr-code.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Album" size="full">
      <div className={styles['wrapper']}>
        <TabBar tabs={TABS} activeTab={tab} onChange={setTab} layoutId="share-album-tab" />

        {tab === 'link' ? (
          <motion.div
            className={styles['link-pane']}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className={styles['qr-card']}>
              <canvas ref={canvasRef} />
            </div>

            <p className={styles['album-label']}>{albumName}</p>
            <p className={styles['hint']}>
              Guests scan this code — or open the link below — to join the album.
            </p>

            <div className={styles['url-row']}>
              <span className={styles['url-text']}>{inviteUrl}</span>
              <button
                type="button"
                onClick={handleCopy}
                className={styles['url-copy-btn']}
                title="Copy invite link"
                aria-label="Copy invite link"
              >
                {copied ? <Check className={styles['icon-sm']} /> : <Copy className={styles['icon-sm']} />}
              </button>
            </div>

            <div className={styles['actions']}>
              <motion.div whileTap={clayTapScale} className={styles['action-wrap']}>
                <Button variant="primary" onClick={handleCopy} fullWidth>
                  <Copy className={styles['icon-btn']} />
                  Copy Link
                </Button>
              </motion.div>
              <motion.div whileTap={clayTapScale} className={styles['action-wrap']}>
                <Button variant="secondary" onClick={handleDownloadQr} fullWidth>
                  <Download className={styles['icon-btn']} />
                  Download QR
                </Button>
              </motion.div>
              <motion.div whileTap={clayTapScale} className={styles['action-wrap']}>
                <Button variant="secondary" onClick={() => setTab('poster')} fullWidth>
                  <FileImage className={styles['icon-btn']} />
                  Create Poster
                </Button>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <A4PosterDesigner
            albumId={albumId}
            albumName={albumName}
            eventName={eventName}
            inviteToken={inviteToken}
          />
        )}
      </div>
    </Modal>
  );
}
